/* All intellectual property rights in the software, code and associated materials ("Software") belong to Leo Learning Limited and its licensors. All rights in the Software are expressly reserved by and to Leo Learning Limited and its licensors. Without limiting the generality of the foregoing, users of the Software are not permitted to (and agree not to) reproduce, copy or otherwise duplicate, and not to distribute, lend or otherwise transfer the Software or any part of it without the express written permission of Leo Learning Limited. Each user also undertakes not to use the Software in any way that would compromise the confidential and proprietary nature of the Software. Each user undertakes to use the Software only in accordance with the terms of the respective user license and not to resell, reproduce, sell, modify the Software in any way and acknowledges that it has no permission to so do. */ // eslint-disable-line max-len
/* global gettext */
import React from 'react'; // eslint-disable-line
import PropTypes from 'prop-types';
import { fromJS, List } from 'immutable';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import ImmutablePropTypes from 'react-immutable-proptypes';

import { MODELS, ACTIONS, can } from 'capabilities';
import { canEdit, isFailedCreation, isPendingOrFailedCreation } from 'models/remoteCreatableObject';
import { TENANTS, OFFICES } from 'actions/collections';
import IfCan from 'components/perms/IfCan';
import Listing from 'components/listing/Listing';
import Dropdown from 'components/form/Dropdown';
import Locked from './Locked';
import StatusIndicator from './StatusIndicator';
import JhanaCompanyStatusIndicator from './JhanaCompanyStatusIndicator';


const TENANT_OFFICE_FIELD = fromJS(
  { displayName: gettext('Office'), type: 'string', name: ['office', 'name'], sortable: true },
);
const TENANT_BASE_FIELDS = fromJS([
  { displayName: gettext('Name'), type: 'string', name: ['name'], sortable: true },
  { displayName: gettext('URL'), type: 'link', name: ['url'], sortable: true },
  {
    displayName: gettext('Status'),
    name: ['status'],
    type: 'custom',
    sortable: true,
    componentFor: tenant => <StatusIndicator object={tenant} />,
  },
  {
    displayName: gettext('Jhana Status'),
    name: ['jhana_company_status'],
    type: 'custom',
    sortable: true,
    componentFor: tenant => <JhanaCompanyStatusIndicator object={tenant} />,
  },
  {
    displayName: gettext('Locked to'),
    type: 'custom',
    componentFor: tenant => <Locked object={tenant} type={TENANTS} />,
  },
  {
    displayName: gettext('Settings'),
    type: 'detail',
    linkText: gettext('Settings'),
    route: '/tenants/${id}', // eslint-disable-line no-template-curly-in-string
    hideIf: isPendingOrFailedCreation,
  },
  {
    displayName: gettext('Passes'),
    type: 'detail',
    linkText: gettext('Passes'),
    route: '/tenants/${id}/passes/', // eslint-disable-line no-template-curly-in-string
    hideIf: isPendingOrFailedCreation,
  },
]);
const TENANT_HIDE_FIELD = fromJS(
  {
    displayName: gettext('Hide'),
    name: ['id'],
    type: 'remove',
    hideIf: tenant => !isFailedCreation(tenant),
  },
);
const makeTenantDeleteField = user => fromJS(
  {
    displayName: gettext('Delete'),
    name: ['id'],
    type: 'remove',
    hideIf: tenant => !canEdit(tenant, user),
  },
);

const makeTenantFields = loggedOnUser =>
  List()
    .concat(can(loggedOnUser, ACTIONS.CREATE, MODELS.OFFICES) ? [TENANT_OFFICE_FIELD] : [])
    .concat(TENANT_BASE_FIELDS)
    .concat(can(loggedOnUser, ACTIONS.UPDATE, MODELS.TENANTS) ? [TENANT_HIDE_FIELD] : [])
    .concat(
      can(loggedOnUser, ACTIONS.DELETE, MODELS.TENANTS)
        ? [makeTenantDeleteField(loggedOnUser)]
        : [],
    );

const DROPDOWN_OFFICE_FILTER = 'tenantsListingOfficeFilter';

export const TenantListing = ({ office, loggedOnUser }) => (
  <div>
    <IfCan action={ACTIONS.CREATE} model={MODELS.TENANTS}>
      <Link className="button" to="/tenants/create/">
        {gettext('Add Client')}
      </Link>
    </IfCan>

    {!loggedOnUser.get('office') && (
      <Dropdown
        dropdownKey={DROPDOWN_OFFICE_FILTER}
        model={OFFICES}
        ordering="name"
        emptyValue={gettext('All offices')}
        selection={office}
      />
    )}

    <Listing
      model={TENANTS}
      fields={makeTenantFields(loggedOnUser)}
      filters={fromJS({ office })}
      removeConfirmationMessage={gettext('Are you sure you wish to remove this client?')}
    />
  </div>
);

TenantListing.propTypes = {
  office: PropTypes.string,
  loggedOnUser: ImmutablePropTypes.map.isRequired,
};

TenantListing.defaultProps = {
  office: '',
};

export function mapStateToProps(state) {
  return {
    loggedOnUser: state.loggedOnUser,
    office: state.ui.getIn(['dropdown', DROPDOWN_OFFICE_FILTER]),
  };
}

export default connect(mapStateToProps)(TenantListing);
